import React, { FC, createContext, useContext, useState, useEffect } from 'react';
import { Path } from '@fenderdigital/api-definitions/play/Path';
import { InstrumentType } from '@fenderdigital/api-definitions/play/Instrument';
import usePathUserData from '../hooks/usePathUserData';
import Library from '../lib/library';

const formatInstrument = (instrument: InstrumentType): string => {
  if (instrument?.includes('-')) {
    return instrument?.split('-')[1];
  }
  return instrument;
};

export interface PathData extends Path {
  formattedInstrument?: string;
}

interface PathContextValue {
  data?: PathData | null;
  error?: string | null;
  isFetching?: boolean;
}

const PathContext = createContext<PathContextValue>({
  data: null,
  error: null,
  isFetching: false,
});

export const usePathContext = (): PathContextValue => useContext(PathContext);

const PathProvider: FC = ({ children }) => {
  const { pathID } = usePathUserData();
  const [data, setData] = useState<Path | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  useEffect(() => {
    if (!pathID) return;
    let retryAgain = 3;
    setIsFetching(true);
    const fetchData = async (): Promise<void> => {
      try {
        const resp = await Library.getPathByID(pathID);
        const formattedInstrument = formatInstrument(resp.instrument);
        setData({ ...resp, formattedInstrument });
      } catch (err) {
        if (retryAgain > 0) {
          retryAgain -= retryAgain;
          fetchData();
        }
        setError(err as string);
      } finally {
        setIsFetching(false);
      }
    };
    fetchData();
  }, [pathID]);

  const ctxVal: PathContextValue = {
    data,
    error,
    isFetching,
  };

  return <PathContext.Provider value={ctxVal}>{children}</PathContext.Provider>;
};

export default PathProvider;
